import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { 
  ArrowLeft, 
  MessageSquare, 
  ThumbsUp, 
  ThumbsDown,
  Clock,
  User,
  Crown,
  Flag,
  Ban,
  Send,
  MoreVertical,
  Sparkles,
  Trash2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

export default function PostDetailPage() {
  const navigate = useNavigate();
  const { postId } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/forum/posts/${postId}`);
      setPost(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Cannot view this post");
        navigate("/community");
      } else {
        toast.error("Failed to load post");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (vote) => {
    try {
      const response = await axios.post(`${API}/forum/posts/${postId}/vote?vote=${vote}`, {});
      setPost({ ...post, vote_count: response.data.vote_count });
    } catch (error) {
      toast.error("Failed to vote");
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) {
      toast.error("Please enter a reply");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/forum/posts/${postId}/replies`, {
        content: replyContent
      });
      
      setPost({
        ...post,
        replies: [...post.replies, response.data]
      });
      setReplyContent("");
      toast.success("Reply posted!");
    } catch (error) {
      toast.error("Failed to post reply");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm("Are you sure you want to delete this post and all its replies?")) return;
    try {
      await axios.delete(`${API}/forum/posts/${postId}`);
      toast.success("Post deleted");
      navigate(-1);
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (!window.confirm("Are you sure you want to delete this reply?")) return;
    try {
      await axios.delete(`${API}/forum/replies/${replyId}`);
      setPost({
        ...post,
        replies: post.replies.filter(r => r.reply_id !== replyId)
      });
      toast.success("Reply deleted");
    } catch (error) {
      toast.error("Failed to delete reply");
    }
  };

  const handleReport = async () => {
    const reason = prompt("Please provide a reason for reporting this post:");
    if (!reason) return;

    try {
      await axios.post(`${API}/forum/posts/${postId}/report`, {
        reason: reason
      });
      toast.success("Report submitted. Thank you for helping keep our community safe.");
    } catch (error) {
      toast.error("Failed to submit report");
    }
  };

  const handleBlockUser = async (userId) => {
    if (!confirm("Are you sure you want to block this user? You won't see their posts or messages.")) return;

    try {
      await axios.post(`${API}/forum/users/${userId}/block`, {});
      toast.success("User blocked");
      navigate("/community");
    } catch (error) {
      toast.error("Failed to block user");
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gold animate-pulse font-serif text-xl">Loading post...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen py-16 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">Post not found</p>
            <Button onClick={() => navigate("/community")} className="mt-4">
              Back to Community
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="post-detail-page">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Back button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Main Post */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex gap-4">
              {/* Vote buttons */}
              <div className="flex flex-col items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-10 w-10"
                  onClick={() => handleVote(1)}
                >
                  <ThumbsUp className="h-5 w-5" />
                </Button>
                <span className={`text-lg font-bold ${post.vote_count > 0 ? 'text-green-500' : post.vote_count < 0 ? 'text-red-500' : ''}`}>
                  {post.vote_count}
                </span>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-10 w-10"
                  onClick={() => handleVote(-1)}
                >
                  <ThumbsDown className="h-5 w-5" />
                </Button>
              </div>

              {/* Post content */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-4">
                  <h1 className="font-serif text-2xl font-bold">{post.title}</h1>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleReport}>
                        <Flag className="h-4 w-4 mr-2" />
                        Report Post
                      </DropdownMenuItem>
                      {post.author?.user_id !== user?.user_id && (
                        <DropdownMenuItem onClick={() => handleBlockUser(post.author?.user_id)}>
                          <Ban className="h-4 w-4 mr-2" />
                          Block User
                        </DropdownMenuItem>
                      )}
                      {user?.is_site_admin && (
                        <DropdownMenuItem 
                          onClick={handleDeletePost}
                          className="text-red-400 focus:text-red-300"
                          data-testid="delete-post-btn"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Post
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <p className="text-foreground whitespace-pre-wrap mb-4">{post.content}</p>

                {/* Author info */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground border-t pt-4">
                  <div 
                    className="flex items-center gap-2 cursor-pointer hover:text-gold"
                    onClick={() => navigate(`/community/user/${post.author?.user_id}`)}
                  >
                    {post.author?.is_founding_member ? (
                      <Sparkles className="h-4 w-4 text-amber-500" />
                    ) : post.author?.is_premium ? (
                      <Crown className="h-4 w-4 text-gold" />
                    ) : (
                      <User className="h-4 w-4" />
                    )}
                    <span className={`font-medium ${post.author?.is_founding_member || post.author?.is_founder ? "text-amber-500" : ""}`}>
                      {post.author?.name || "Anonymous"}
                    </span>
                    {post.author?.username && (
                      <span className="text-xs text-muted-foreground">@{post.author.username}</span>
                    )}
                    {post.author?.is_founder && (
                      <Badge className="bg-primary text-primary-foreground border-0 text-[10px] px-1.5 py-0">
                        Founder
                      </Badge>
                    )}
                    {post.author?.is_founding_member && (
                      <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black border-0 text-[10px] px-1.5 py-0">
                        Founding Member
                      </Badge>
                    )}
                    {post.author?.badges?.filter(b => b.badge_id !== 'founding_member').map(badge => (
                      <Badge key={badge.badge_id || badge.id} variant="outline" className="text-xs" style={{ color: badge.color, borderColor: badge.color }}>
                        {badge.name}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{formatTimeAgo(post.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reply Form */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <Textarea
                placeholder="Write a reply..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                className="flex-1"
                rows={3}
                data-testid="reply-input"
              />
              <Button 
                onClick={handleReply}
                disabled={submitting}
                className="bg-gold text-primary-foreground self-end"
                data-testid="submit-reply-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                {submitting ? "Posting..." : "Reply"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Replies */}
        <div className="space-y-4">
          <h2 className="font-serif text-lg font-bold flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-gold" />
            {post.replies?.length || 0} Replies
          </h2>

          {post.replies?.length === 0 ? (
            <Card>
              <CardContent className="py-6 text-center text-muted-foreground">
                No replies yet. Be the first to reply!
              </CardContent>
            </Card>
          ) : (
            post.replies?.map(reply => (
              <Card key={reply.reply_id} data-testid={`reply-${reply.reply_id}`}>
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    {/* Vote */}
                    <div className="flex flex-col items-center text-sm">
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <ThumbsUp className="h-3 w-3" />
                      </Button>
                      <span className={reply.vote_count > 0 ? 'text-green-500' : reply.vote_count < 0 ? 'text-red-500' : ''}>
                        {reply.vote_count}
                      </span>
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <ThumbsDown className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <p className="whitespace-pre-wrap mb-3">{reply.content}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          {reply.author?.is_founding_member ? (
                            <Sparkles className="h-3 w-3 text-amber-500" />
                          ) : reply.author?.is_premium ? (
                            <Crown className="h-3 w-3 text-gold" />
                          ) : (
                            <User className="h-3 w-3" />
                          )}
                          <span className={reply.author?.is_founding_member || reply.author?.is_founder ? "text-amber-500 font-medium" : ""}>
                            {reply.author?.name || "Anonymous"}
                          </span>
                          {reply.author?.username && (
                            <span className="text-[10px] text-muted-foreground">@{reply.author.username}</span>
                          )}
                          {reply.author?.is_founder && (
                            <Badge className="bg-primary text-primary-foreground border-0 text-[10px] px-1 py-0">
                              Founder
                            </Badge>
                          )}
                          {reply.author?.is_founding_member && (
                            <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black border-0 text-[10px] px-1 py-0">
                              Founding
                            </Badge>
                          )}
                        </div>
                        <span>{formatTimeAgo(reply.created_at)}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    {reply.author?.user_id !== user?.user_id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6">
                            <MoreVertical className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleBlockUser(reply.author?.user_id)}>
                            <Ban className="h-4 w-4 mr-2" />
                            Block User
                          </DropdownMenuItem>
                          {user?.is_site_admin && (
                            <DropdownMenuItem 
                              onClick={() => handleDeleteReply(reply.reply_id)}
                              className="text-red-400 focus:text-red-300"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Reply
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {reply.author?.user_id === user?.user_id && user?.is_site_admin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => handleDeleteReply(reply.reply_id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
